import { type FocusEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNotify } from 'react-admin';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

export interface ExperimentItem {
  id: string;
  experimentId: string;
  labSampleId?: string | null;
  yourSampleId?: string | null;
  analysis?: string | null;
  analysisVariation?: string | null;
  testMethod?: string | null;
  unit: string;
  sampleResult?: string | null;
  testValue: string;
  loq?: string | null;
  inSpec?: string | null;
  testResults?: string | null;
}

export interface ExperimentRecord {
  id: string;
  reference?: string | null;
  labSampleId?: string | null;
  yourSampleId?: string | null;
  sampleDescription: string;
  batchNumber: string;
  description?: string | null;
  qcType?: string | null;
  sampleInformation?: string | null;
  templateType: string;
  timeRecord: string;
  exResult: number;
  createBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExperimentDataDetailProps {
  experiment: ExperimentRecord;
  items: ExperimentItem[];
  loading: boolean;
  onBack: () => void;
  onUpdated?: (experiment: ExperimentRecord, items: ExperimentItem[]) => void;
}

type AQEditableField =
  | 'labSampleId'
  | 'yourSampleId'
  | 'analysis'
  | 'analysisVariation'
  | 'sampleResult'
  | 'testValue'
  | 'unit'
  | 'inSpec';

type BNEditableField = 'analysis' | 'testMethod' | 'unit' | 'loq' | 'testValue';

type EditableField = AQEditableField | BNEditableField;

interface BNDisplayRow {
  key: string;
  reference: string;
  unit: string;
  loq: string;
  testMethod: string;
  testValue: string;
  primaryItemIndex: number;
  primaryItemId: string;
}

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const textOrDash = (value: string | null | undefined) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '-';
  }

  return value;
};

const mergeBnLabel = (value: string | null | undefined) => {
  const text = textOrDash(value);
  if (text === '-') return '-';

  return text.replace(/-\d+$/, '').trim();
};

const compareItemOrder = (leftId: string, rightId: string) => {
  const left = Number.parseInt(leftId, 10);
  const right = Number.parseInt(rightId, 10);

  const leftIsNumber = Number.isFinite(left);
  const rightIsNumber = Number.isFinite(right);

  if (leftIsNumber && rightIsNumber) {
    return left - right;
  }

  if (leftIsNumber) return -1;
  if (rightIsNumber) return 1;

  return leftId.localeCompare(rightId);
};

const aqEditableFields: AQEditableField[] = [
  'labSampleId',
  'yourSampleId',
  'analysis',
  'analysisVariation',
  'sampleResult',
  'testValue',
  'unit',
  'inSpec',
];

const bnEditableFields: BNEditableField[] = ['analysis', 'testMethod', 'unit', 'loq', 'testValue'];

const isAQField = (field: EditableField): field is AQEditableField => aqEditableFields.includes(field as AQEditableField);

const isBNField = (field: EditableField): field is BNEditableField => bnEditableFields.includes(field as BNEditableField);

const mergeBnItems = (items: ExperimentItem[]): BNDisplayRow[] => {
  const grouped = new Map<
    string,
    {
      reference: string;
      unit: string;
      loq: string;
      testMethod: string;
      testValues: string[];
      primaryItemIndex: number;
      primaryItemId: string;
    }
  >();

  items.forEach((item, index) => {
    const reference = mergeBnLabel(item.analysis);
    const key = [reference, textOrDash(item.unit), textOrDash(item.loq), textOrDash(item.testMethod)].join('||');
    const existing = grouped.get(key);
    const testValue = textOrDash(item.testValue);

    if (existing) {
      if (testValue !== '-') {
        existing.testValues.push(testValue);
      }
      return;
    }

    grouped.set(key, {
      reference,
      unit: textOrDash(item.unit),
      loq: textOrDash(item.loq),
      testMethod: textOrDash(item.testMethod),
      testValues: testValue === '-' ? [] : [testValue],
      primaryItemIndex: index,
      primaryItemId: item.id,
    });
  });

  return Array.from(grouped.entries()).map(([key, entry]) => ({
    key,
    reference: entry.reference,
    unit: entry.unit,
    loq: entry.loq,
    testMethod: entry.testMethod,
    testValue: entry.testValues.length > 0 ? entry.testValues.join(', ') : '-',
    primaryItemIndex: entry.primaryItemIndex,
    primaryItemId: entry.primaryItemId,
  }));
};

const getRowKey = (item: ExperimentItem) => `${item.experimentId}-${item.id}`;

export default function ExperimentDataDetail({ experiment, items, loading, onBack, onUpdated }: ExperimentDataDetailProps) {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
  const notify = useNotify();

  const [draftItems, setDraftItems] = useState<ExperimentItem[]>(() => [...items].sort((left, right) => compareItemOrder(left.id, right.id)));
  const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
  const [savingItemKey, setSavingItemKey] = useState<string | null>(null);
  const draftItemsRef = useRef<ExperimentItem[]>(draftItems);

  useEffect(() => {
    const nextItems = [...items].sort((left, right) => compareItemOrder(left.id, right.id));
    setDraftItems(nextItems);
    draftItemsRef.current = nextItems;
    setEditingItemKey(null);
    setSavingItemKey(null);
  }, [items]);

  useEffect(() => {
    draftItemsRef.current = draftItems;
  }, [draftItems]);

  const orderedItems = useMemo(() => [...draftItems].sort((left, right) => compareItemOrder(left.id, right.id)), [draftItems]);
  const isAQ = (experiment.templateType || '').toUpperCase() === 'AQ';
  const isBN = (experiment.templateType || '').toUpperCase() === 'BN';
  const bnItems = useMemo(() => (isBN ? mergeBnItems(orderedItems) : []), [isBN, orderedItems]);
  const displayCount = isBN ? bnItems.length : orderedItems.length;

  const updateItemField = (index: number, field: EditableField, value: string) => {
    if (!isAQField(field) && !isBNField(field)) {
      return;
    }

    setDraftItems((current) => {
      const nextItems = current.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item));
      draftItemsRef.current = nextItems;
      return nextItems;
    });
  };

  const saveExperiment = async (rowKey: string, itemIndex?: number) => {
    if (savingItemKey) return;

    const currentItems = draftItemsRef.current;
    const resolvedIndex = typeof itemIndex === 'number' ? itemIndex : currentItems.findIndex((item) => getRowKey(item) === rowKey);
    const item = currentItems[resolvedIndex];

    if (!item) return;

    setSavingItemKey(rowKey);

    try {
      const token = localStorage.getItem('token');
      const experimentId = Number.parseInt(item.experimentId, 10);
      const itemId = Number.parseInt(item.id, 10);

      const response = await fetch(`${API_URL}/experimentresultitems/${experimentId}/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ExperimentId: experimentId,
          LabSampleId: item.labSampleId,
          YourSampleId: item.yourSampleId,
          Analysis: item.analysis,
          AnalysisVariation: item.analysisVariation,
          TestMethod: item.testMethod,
          Unit: item.unit,
          SampleResult: item.sampleResult,
          TestValue: item.testValue,
          LOQ: item.loq,
          InSpec: item.inSpec,
          TestResults: item.testResults,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }

      const nextItems = [...currentItems];
      draftItemsRef.current = nextItems;
      setDraftItems(nextItems);
      setEditingItemKey(null);
      onUpdated?.(experiment, nextItems);
      notify('Experiment item saved successfully', { type: 'success' });
    } catch (error) {
      setEditingItemKey(rowKey);
      notify(`Save failed: ${(error as Error).message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setSavingItemKey(null);
    }
  };

  const handleRowBlur = (rowKey: string) => (event: FocusEvent<HTMLTableRowElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    if (editingItemKey !== rowKey) return;

    setEditingItemKey(null);
    window.setTimeout(() => {
      void saveExperiment(rowKey);
    }, 0);
  };

  const handleRowBlurWithIndex = (rowKey: string, itemIndex: number) => (event: FocusEvent<HTMLTableRowElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    if (editingItemKey !== rowKey) return;

    setEditingItemKey(null);
    window.setTimeout(() => {
      void saveExperiment(rowKey, itemIndex);
    }, 0);
  };

  const aqRows = orderedItems.map((item, index) => {
    const rowKey = getRowKey(item);
    const isEditing = editingItemKey === rowKey;
    const isSaving = savingItemKey === rowKey;

    return (
      <tr
        key={rowKey}
        className={`border-t border-slate-100 ${isEditing ? 'bg-slate-50/90' : 'hover:bg-slate-50/70'}`}
        onDoubleClick={() => setEditingItemKey(rowKey)}
        onBlur={handleRowBlur(rowKey)}
      >
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              autoFocus
              value={draftItems[index]?.labSampleId ?? ''}
              onChange={(event) => updateItemField(index, 'labSampleId', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.labSampleId)}</span>
          )}
        </td>
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              value={draftItems[index]?.yourSampleId ?? ''}
              onChange={(event) => updateItemField(index, 'yourSampleId', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.yourSampleId)}</span>
          )}
        </td>
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              value={draftItems[index]?.analysis ?? ''}
              onChange={(event) => updateItemField(index, 'analysis', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.analysis)}</span>
          )}
        </td>
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              value={draftItems[index]?.analysisVariation ?? ''}
              onChange={(event) => updateItemField(index, 'analysisVariation', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.analysisVariation)}</span>
          )}
        </td>
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              value={draftItems[index]?.sampleResult ?? ''}
              onChange={(event) => updateItemField(index, 'sampleResult', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.sampleResult)}</span>
          )}
        </td>
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              value={draftItems[index]?.testValue ?? ''}
              onChange={(event) => updateItemField(index, 'testValue', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.testValue)}</span>
          )}
        </td>
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              value={draftItems[index]?.unit ?? ''}
              onChange={(event) => updateItemField(index, 'unit', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.unit)}</span>
          )}
        </td>
        <td className="px-4 py-2">
          {isEditing ? (
            <Input
              value={draftItems[index]?.inSpec ?? ''}
              onChange={(event) => updateItemField(index, 'inSpec', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{textOrDash(item.inSpec)}</span>
          )}
        </td>
      </tr>
    );
  });

  const bnRows = bnItems.map((row) => {
    const item = orderedItems[row.primaryItemIndex];
    if (!item) return null;

    const rowKey = getRowKey(item);
    const isEditing = editingItemKey === rowKey;
    const isSaving = savingItemKey === rowKey;

    return (
      <tr
        key={row.key}
        className={`border-t border-slate-100 ${isEditing ? 'bg-slate-50/90' : 'hover:bg-slate-50/70'}`}
        onDoubleClick={() => setEditingItemKey(rowKey)}
        onBlur={handleRowBlurWithIndex(rowKey, row.primaryItemIndex)}
      >
        <td className="px-4 py-2 break-words">
          {isEditing ? (
            <Input
              autoFocus
              value={draftItems[row.primaryItemIndex]?.analysis ?? ''}
              onChange={(event) => updateItemField(row.primaryItemIndex, 'analysis', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{row.reference}</span>
          )}
        </td>
        <td className="px-4 py-2 break-words">
          {isEditing ? (
            <Input
              value={draftItems[row.primaryItemIndex]?.unit ?? ''}
              onChange={(event) => updateItemField(row.primaryItemIndex, 'unit', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{row.unit}</span>
          )}
        </td>
        <td className="px-4 py-2 break-words">
          {isEditing ? (
            <Input
              value={draftItems[row.primaryItemIndex]?.loq ?? ''}
              onChange={(event) => updateItemField(row.primaryItemIndex, 'loq', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{row.loq}</span>
          )}
        </td>
        <td className="px-4 py-2 break-words">
          {isEditing ? (
            <Input
              value={draftItems[row.primaryItemIndex]?.testMethod ?? ''}
              onChange={(event) => updateItemField(row.primaryItemIndex, 'testMethod', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{row.testMethod}</span>
          )}
        </td>
        <td className="px-4 py-2 break-words">
          {isEditing ? (
            <Input
              value={draftItems[row.primaryItemIndex]?.testValue ?? ''}
              onChange={(event) => updateItemField(row.primaryItemIndex, 'testValue', event.target.value)}
              disabled={isSaving}
              className="h-9 bg-white"
            />
          ) : (
            <span>{row.testValue}</span>
          )}
        </td>
      </tr>
    );
  });

  const generalRows = orderedItems.map((item) => (
    <tr key={getRowKey(item)} className="border-t border-slate-100 hover:bg-slate-50/70">
      <td className="px-4 py-2">{textOrDash(item.analysis)}</td>
      <td className="px-4 py-2">{textOrDash(item.analysisVariation)}</td>
      <td className="px-4 py-2">{textOrDash(item.testMethod)}</td>
      <td className="px-4 py-2">{textOrDash(item.sampleResult)}</td>
      <td className="px-4 py-2">{textOrDash(item.testValue)}</td>
      <td className="px-4 py-2">{textOrDash(item.unit)}</td>
      <td className="px-4 py-2">{textOrDash(item.loq)}</td>
      <td className="px-4 py-2">{textOrDash(item.inSpec)}</td>
    </tr>
  ));

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b bg-gradient-to-r from-slate-50 to-white px-5 py-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">Experiment Detail</h2>
          <p className="text-sm text-slate-500">Template: {textOrDash(experiment.templateType)}</p>
          <p className="text-sm text-slate-500">
            Total Items: {items.length}
            {isBN ? ` (Displayed: ${displayCount})` : ''}
          </p>
        </div>

        <Button onClick={onBack} variant="outline" className="flex w-fit items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="border-b border-slate-100 px-5 py-5">
        {isAQ ? (
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-3">
            <div className="text-sm">
              <span className="font-medium text-slate-500">Batch Number:</span> <span className="text-slate-900">{textOrDash(experiment.batchNumber)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Sample Description:</span> <span className="text-slate-900">{textOrDash(experiment.sampleDescription)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Template Type:</span> <span className="text-slate-900">{textOrDash(experiment.templateType)}</span>
            </div>
          </div>
        ) : isBN ? (
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-3">
            <div className="text-sm">
              <span className="font-medium text-slate-500">SampleNo:</span> <span className="text-slate-900">{textOrDash(experiment.reference)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Batch No.:</span> <span className="text-slate-900">{textOrDash(experiment.batchNumber)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Description:</span> <span className="text-slate-900">{textOrDash(experiment.description)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Type:</span> <span className="text-slate-900">{textOrDash(experiment.templateType)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">QC Type:</span> <span className="text-slate-900">{textOrDash(experiment.qcType)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Sample Information:</span> <span className="text-slate-900">{textOrDash(experiment.sampleInformation)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Time:</span> <span className="text-slate-900">{formatDateTime(experiment.timeRecord)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Sample Description:</span> <span className="text-slate-900">{textOrDash(experiment.sampleDescription)}</span>
            </div>
          </div>
        ) : (
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-3">
            <div className="text-sm">
              <span className="font-medium text-slate-500">Batch Number:</span> <span className="text-slate-900">{textOrDash(experiment.batchNumber)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Lab Sample ID:</span> <span className="text-slate-900">{textOrDash(experiment.labSampleId)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Your Sample ID:</span> <span className="text-slate-900">{textOrDash(experiment.yourSampleId)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Sample Description:</span> <span className="text-slate-900">{textOrDash(experiment.sampleDescription)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-slate-500">Time Record:</span> <span className="text-slate-900">{formatDateTime(experiment.timeRecord)}</span>
            </div>
            <div className="text-sm md:col-span-3">
              <span className="font-medium text-slate-500">Description:</span> <span className="text-slate-900">{textOrDash(experiment.description)}</span>
            </div>
            <div className="text-sm md:col-span-3">
              <span className="font-medium text-slate-500">Sample Information:</span> <span className="text-slate-900">{textOrDash(experiment.sampleInformation)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading experiment result items...</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No experiment result items found.</div>
        ) : (
          <table className="min-w-full table-fixed text-sm">
            <thead className="bg-slate-50 text-slate-600">
              {isAQ ? (
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Lab Sample ID</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Your Sample ID</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis Variation</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Result</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Value</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Unit</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">In Spec</th>
                </tr>
              ) : isBN ? (
                <tr>
                  <th className="w-[30%] whitespace-nowrap px-4 py-3 text-left font-medium">Reference</th>
                  <th className="w-[15%] whitespace-nowrap px-4 py-3 text-left font-medium">Units</th>
                  <th className="w-[15%] whitespace-nowrap px-4 py-3 text-left font-medium">LOQ</th>
                  <th className="w-[20%] whitespace-nowrap px-4 py-3 text-left font-medium">Method</th>
                  <th className="w-[20%] whitespace-nowrap px-4 py-3 text-left font-medium">Test Value</th>
                </tr>
              ) : (
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis Variation</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Test Method</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Sample Result</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Test Value</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Unit</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">LOQ</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">In Spec</th>
                </tr>
              )}
            </thead>
            <tbody>{isAQ ? aqRows : isBN ? bnRows : generalRows}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
